/** Short function words kept lowercase inside an English title. */
const MINOR_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'into', 'nor',
  'of', 'on', 'or', 'per', 'the', 'to', 'via', 'vs', 'with',
]);

/** Tokens after which the next word starts a new phrase ("Avoided Emissions · Outside …"). */
const PHRASE_BREAK = /^[·—–:|/-]$/;

/**
 * English title case for headings: "Avoided emissions · outside GHG inventory"
 * → "Avoided Emissions · Outside GHG Inventory". Minor words stay lowercase
 * unless they open a phrase or end the title; words that already contain a
 * capital (GHG, CO2, RECYCLEE, IFRS) are left untouched.
 */
export function toTitleCase(text: string): string {
  const tokens = text.split(/(\s+)/);
  const wordIndexes = tokens.map((tok, i) => (/\S/.test(tok) ? i : -1)).filter((i) => i >= 0);
  const lastWord = wordIndexes[wordIndexes.length - 1];
  let phraseStart = true;

  return tokens
    .map((token, i) => {
      if (!/\S/.test(token)) return token;
      if (PHRASE_BREAK.test(token)) {
        phraseStart = true;
        return token;
      }
      const opensPhrase = phraseStart;
      phraseStart = false;
      if (/\p{Lu}/u.test(token)) return token;
      const bare = token.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, '').toLowerCase();
      if (!opensPhrase && i !== lastWord && MINOR_WORDS.has(bare)) return token;
      return token.replace(/^([^\p{L}]*)(\p{L})/u, (_, lead: string, ch: string) => lead + ch.toUpperCase());
    })
    .join('');
}

/** Title case in English; other app languages have no letter case and pass through. */
export function headingCase(text: string, lang: string | undefined): string {
  return (lang || 'en').toLowerCase().startsWith('en') ? toTitleCase(text) : text;
}
